import { InformationCircle } from "@ignite-analytics/icons";
import { Alert, debounce, FormControl, FormHelperText, Stack, TextField, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import React, { useRef, useEffect } from "react";
import { useIntl } from "react-intl";

import { initialValue } from "../../Form/helpers";
import { QuestionProps } from "../interfaces";
import { StyledRating } from "../StyledRating";

const RatingWithCommentQuestion: React.FC<QuestionProps> = ({ question, saveDraft, ...props }) => {
    const { formatMessage } = useIntl();
    const { values, getFieldMeta, errors, setFieldValue } = useFormikContext<Record<string, initialValue>>();
    const error = errors[question.id];
    const formValue = values[question.id];

    const { touched } = getFieldMeta(`${question.id}.value`);

    const saveDraftRef = useRef(saveDraft);
    useEffect(() => {
        saveDraftRef.current = saveDraft;
    }, [saveDraft]);

    const debounceFunc = useRef(
        debounce((comment: string) => {
            saveDraftRef.current(undefined, undefined, comment);
        }, 1000)
    ).current;

    if (!formValue || !question.maxRating) return null;

    const ratingValue = Number(formValue.value);
    const encourageComment = ratingValue === 1 || ratingValue === 2;

    return (
        <Stack spacing={2} width="100%">
            <FormControl sx={{ width: "100%" }} error={touched && Boolean(error)}>
                <StyledRating
                    max={question.maxRating}
                    value={Number(formValue.value)}
                    disabled={props.disabled}
                    onChange={(_, v) => {
                        if (!v) return;
                        setFieldValue(question.id, {
                            value: v.toString(),
                            fileNames: formValue.fileNames,
                            comment: formValue.comment,
                        });
                        saveDraft([v.toString()], undefined, formValue.comment);
                    }}
                    size="medium"
                    showLabels
                />
                <FormHelperText>
                    <Stack direction="row" justifyContent="flex-start">
                        <Typography>{touched && error ? error.value : ""}</Typography>
                    </Stack>
                </FormHelperText>
            </FormControl>

            <Stack spacing={1}>
                <Typography variant="textSm" fontWeight={500}>
                    {formatMessage({ defaultMessage: "Why did you give this score?" })}
                </Typography>
                {encourageComment && (
                    <Alert severity="info" icon={<InformationCircle fontSize="small" />} sx={{ py: 0.5 }}>
                        <Typography variant="textSm">
                            {formatMessage({
                                defaultMessage:
                                    "Since this is a lower score, a brief comment explaining why is really helpful.",
                            })}
                        </Typography>
                    </Alert>
                )}
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={formValue.comment}
                    disabled={props.disabled}
                    onChange={(event) => {
                        setFieldValue(question.id, {
                            value: formValue.value,
                            fileNames: formValue.fileNames,
                            comment: event.target.value,
                        });
                        debounceFunc(event.target.value);
                    }}
                    placeholder={formatMessage({ defaultMessage: "Write here..." })}
                />
            </Stack>
        </Stack>
    );
};

export default RatingWithCommentQuestion;
